// Persistent settings store — audio, controls, graphics, gameplay,
// appearance (theme) and language. Synced to localStorage so settings
// survive page reloads.

import { create } from 'zustand';
import { isDevSilent } from '../audio/devSilent.js';
import { type Locale, browserLocale, isLocale } from '../i18n/locale.js';
import type { PauseOnDanger } from '../pause/detectDangers.js';
import { type ThemePreference, isThemePreference } from '../theme/theme.js';

/** Milestone cinematics (pacing spec §6 C2): every milestone, big moments only, or none. */
export type CinematicsSetting = 'all' | 'big' | 'off';

export function isCinematicsSetting(v: unknown): v is CinematicsSetting {
  return v === 'all' || v === 'big' || v === 'off';
}

/** Player-facing combat visualization mode (spec §1a). */
export type CombatOverlayMode = 'off' | 'drawer' | 'modal+drawer';

export interface SettingsState {
  // Audio
  masterVolume: number; // 0-1
  musicVolume: number; // 0-1
  sfxVolume: number; // 0-1
  // Controls
  scrollSpeed: number; // 1-10
  // Graphics
  particleEffects: boolean;
  /** Drifting atmospheric clouds and their ground shadows (default on). */
  showClouds: boolean;
  showFauna: boolean;
  showGrid: boolean;
  /** Render the board at 2x size with denser scatter. */
  doubleBoard: boolean;
  /** Tint + border tiles by controlling player. */
  showTerritory: boolean;
  /** Animate the cosmetic day/night cycle (with night torch/campfire glows). */
  showDayNight: boolean;
  /** Rounded, smooth-normaled unit voxels — costs several× the vertices. */
  /** How units are drawn: blocky voxels, smoothed voxels, or authored 3D models (smooth where none exists yet). */
  unitQuality: UnitQuality;
  /** `unitQuality !== 'voxel'`, kept for the callers that only ask hi-fi or not. */
  hiFiUnits: boolean;
  /** Draw the 3D camera's ground footprint on the minimap (default on). */
  minimapViewBox: boolean;
  // Gameplay
  autosaveEnabled: boolean;
  showTileTooltips: boolean;
  /** Controls the combat visualization surface (3D jitter always on when ≠ 'off'). */
  combatOverlayMode: CombatOverlayMode;
  /** Pause single-player when the tab is hidden or the window loses focus. */
  autoPauseWhenAway: boolean;
  /** Pause single-player when your cities are captured (major) or attacked (all). */
  pauseOnDanger: PauseOnDanger;
  /** Which milestones play a short cinematic (default: big moments only). */
  cinematics: CinematicsSetting;
  // Appearance + language
  /** Light / dark / follow the OS (default). Applied by useApplyTheme. */
  theme: ThemePreference;
  /** UI language; defaults to the browser's (navigator.language). */
  locale: Locale;

  // Actions
  setMasterVolume: (v: number) => void;
  setMusicVolume: (v: number) => void;
  setSfxVolume: (v: number) => void;
  setScrollSpeed: (v: number) => void;
  setParticleEffects: (v: boolean) => void;
  setShowClouds: (v: boolean) => void;
  setShowFauna: (v: boolean) => void;
  setShowGrid: (v: boolean) => void;
  setDoubleBoard: (v: boolean) => void;
  setShowTerritory: (v: boolean) => void;
  setShowDayNight: (v: boolean) => void;
  setHiFiUnits: (v: boolean) => void;
  setUnitQuality: (q: UnitQuality) => void;
  setMinimapViewBox: (v: boolean) => void;
  setAutosaveEnabled: (v: boolean) => void;
  setShowTileTooltips: (v: boolean) => void;
  setCombatOverlayMode: (mode: CombatOverlayMode) => void;
  setAutoPauseWhenAway: (v: boolean) => void;
  setPauseOnDanger: (mode: PauseOnDanger) => void;
  setCinematics: (mode: CinematicsSetting) => void;
  setTheme: (theme: ThemePreference) => void;
  setLocale: (locale: Locale) => void;
}

const STORAGE_KEY = 'aod-settings';

function load(): Partial<SettingsState> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Partial<SettingsState>) : {};
  } catch {
    return {};
  }
}

export type UnitQuality = 'voxel' | 'smooth' | 'mesh';

const UNIT_QUALITIES: readonly UnitQuality[] = ['voxel', 'smooth', 'mesh'];

function savedQuality(saved: { unitQuality?: unknown; hiFiUnits?: unknown }): UnitQuality {
  const q = UNIT_QUALITIES.find((x) => x === saved.unitQuality);
  if (q) return q;
  return saved.hiFiUnits === true ? 'smooth' : 'voxel';
}

function persist(state: SettingsState) {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        masterVolume: state.masterVolume,
        musicVolume: state.musicVolume,
        sfxVolume: state.sfxVolume,
        scrollSpeed: state.scrollSpeed,
        particleEffects: state.particleEffects,
        showClouds: state.showClouds,
        showFauna: state.showFauna,
        showGrid: state.showGrid,
        doubleBoard: state.doubleBoard,
        showTerritory: state.showTerritory,
        showDayNight: state.showDayNight,
        hiFiUnits: state.hiFiUnits,
        unitQuality: state.unitQuality,
        minimapViewBox: state.minimapViewBox,
        autosaveEnabled: state.autosaveEnabled,
        showTileTooltips: state.showTileTooltips,
        combatOverlayMode: state.combatOverlayMode,
        autoPauseWhenAway: state.autoPauseWhenAway,
        pauseOnDanger: state.pauseOnDanger,
        cinematics: state.cinematics,
        theme: state.theme,
        locale: state.locale,
      }),
    );
  } catch {
    /* quota exceeded — silently skip */
  }
}

const saved = load();

// Dev machines: ALWAYS start muted on localhost — music autoplaying in
// every dev session is a nuisance, and a volume saved by any earlier
// session (or before the dev-mute default existed) used to resurrect it.
// Dragging the slider in an open Settings modal still unmutes *that*
// session; the next dev boot is silent again. Production keeps the
// audible default.
const devStart = isDevSilent() ? 0 : (saved.masterVolume ?? 0.7);

export const useSettings = create<SettingsState>()((set) => ({
  masterVolume: devStart,
  musicVolume: saved.musicVolume ?? 0.5,
  sfxVolume: saved.sfxVolume ?? 0.8,
  scrollSpeed: saved.scrollSpeed ?? 5,
  particleEffects: saved.particleEffects ?? true,
  showClouds: saved.showClouds ?? true,
  showFauna: saved.showFauna ?? true,
  showGrid: saved.showGrid ?? false,
  doubleBoard: saved.doubleBoard ?? false,
  showTerritory: saved.showTerritory ?? true,
  showDayNight: saved.showDayNight ?? true,
  // Saves from before the three levels stored only the hi-fi flag: true meant the smooth look.
  unitQuality: savedQuality(saved),
  hiFiUnits: savedQuality(saved) !== 'voxel',
  minimapViewBox: saved.minimapViewBox ?? true,
  autosaveEnabled: saved.autosaveEnabled ?? true,
  showTileTooltips: saved.showTileTooltips ?? true,
  combatOverlayMode: saved.combatOverlayMode ?? 'drawer',
  autoPauseWhenAway: saved.autoPauseWhenAway ?? true,
  pauseOnDanger: saved.pauseOnDanger ?? 'major',
  cinematics: isCinematicsSetting(saved.cinematics) ? saved.cinematics : 'big',
  theme: isThemePreference(saved.theme) ? saved.theme : 'system',
  locale: isLocale(saved.locale) ? saved.locale : browserLocale(),

  setMasterVolume: (v) =>
    set((s) => {
      const ns = { ...s, masterVolume: v };
      persist(ns);
      return ns;
    }),
  setMusicVolume: (v) =>
    set((s) => {
      const ns = { ...s, musicVolume: v };
      persist(ns);
      return ns;
    }),
  setSfxVolume: (v) =>
    set((s) => {
      const ns = { ...s, sfxVolume: v };
      persist(ns);
      return ns;
    }),
  setScrollSpeed: (v) =>
    set((s) => {
      const ns = { ...s, scrollSpeed: v };
      persist(ns);
      return ns;
    }),
  setParticleEffects: (v) =>
    set((s) => {
      const ns = { ...s, particleEffects: v };
      persist(ns);
      return ns;
    }),
  setShowClouds: (v) =>
    set((s) => {
      const ns = { ...s, showClouds: v };
      persist(ns);
      return ns;
    }),
  setShowFauna: (v) =>
    set((s) => {
      const ns = { ...s, showFauna: v };
      persist(ns);
      return ns;
    }),
  setShowGrid: (v) =>
    set((s) => {
      const ns = { ...s, showGrid: v };
      persist(ns);
      return ns;
    }),
  setDoubleBoard: (v) =>
    set((s) => {
      const ns = { ...s, doubleBoard: v };
      persist(ns);
      return ns;
    }),
  setShowTerritory: (v) =>
    set((s) => {
      const ns = { ...s, showTerritory: v };
      persist(ns);
      return ns;
    }),
  setShowDayNight: (v) =>
    set((s) => {
      const ns = { ...s, showDayNight: v };
      persist(ns);
      return ns;
    }),
  setHiFiUnits: (v) =>
    set((s) => {
      const quality: UnitQuality = v
        ? s.unitQuality === 'voxel'
          ? 'smooth'
          : s.unitQuality
        : 'voxel';
      const ns = { ...s, hiFiUnits: v, unitQuality: quality };
      persist(ns);
      return ns;
    }),
  setUnitQuality: (q) =>
    set((s) => {
      const ns = { ...s, unitQuality: q, hiFiUnits: q !== 'voxel' };
      persist(ns);
      return ns;
    }),
  setMinimapViewBox: (v) =>
    set((s) => {
      const ns = { ...s, minimapViewBox: v };
      persist(ns);
      return ns;
    }),
  setAutosaveEnabled: (v) =>
    set((s) => {
      const ns = { ...s, autosaveEnabled: v };
      persist(ns);
      return ns;
    }),
  setShowTileTooltips: (v) =>
    set((s) => {
      const ns = { ...s, showTileTooltips: v };
      persist(ns);
      return ns;
    }),
  setCombatOverlayMode: (mode) =>
    set((s) => {
      const ns = { ...s, combatOverlayMode: mode };
      persist(ns);
      return ns;
    }),
  setAutoPauseWhenAway: (v) =>
    set((s) => {
      const ns = { ...s, autoPauseWhenAway: v };
      persist(ns);
      return ns;
    }),
  setPauseOnDanger: (mode) =>
    set((s) => {
      const ns = { ...s, pauseOnDanger: mode };
      persist(ns);
      return ns;
    }),
  setCinematics: (mode) =>
    set((s) => {
      const ns = { ...s, cinematics: mode };
      persist(ns);
      return ns;
    }),
  setTheme: (theme) =>
    set((s) => {
      const ns = { ...s, theme };
      persist(ns);
      return ns;
    }),
  setLocale: (locale) =>
    set((s) => {
      const ns = { ...s, locale };
      persist(ns);
      return ns;
    }),
}));
